import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { isValidEmail, normalizeEmail } from "@/lib/validate";
import SeoHead from "@/components/SeoHead";
import { SITE_URL } from "@/seo/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import Header from "@/components/Header";
import { StepProgress } from "@/components/StepProgress";
import FileUpload from "@/components/FileUpload";
import SignaturePadField from "@/components/SignaturePadField";
import AutocompleteInput from "@/components/AutocompleteInput";
import { searchBrands, searchModels } from "@/data/vehicles";
import { isDateDisabled, getBookingHint, toLocalDateString, parseLocalDateString } from "@/lib/booking";
import {
  ArrowRight, ArrowLeft, CheckCircle, SpinnerGap, MagicWand,
  CarProfile, Warning, FileText, ShieldCheck, PencilSimpleLine, CalendarBlank, CreditCard, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = [
  "Fahrzeugdaten",
  "Schaden erfassen",
  "Dokumente hochladen",
  "Versicherung",
  "Abtretungserklärung",
  "Terminbuchung",
  "Zahlung",
];

const GLASS_OPTIONS = [
  { v: "frontscheibe", label: "Frontscheibe" },
  { v: "seitenscheibe", label: "Seitenscheibe" },
  { v: "heckscheibe", label: "Heckscheibe" },
  { v: "sonstiges", label: "Sonstiges" },
];

const DAMAGE_OPTIONS = [
  { v: "steinschlag", label: "Steinschlag" },
  { v: "riss", label: "Riss" },
  { v: "bruch", label: "Bruch" },
  { v: "sonstiges", label: "Sonstiges" },
];

const TIME_SLOTS = ["09:00", "11:00", "13:00", "15:00", "17:00"];

export default function ClaimWizard() {
  const navigate = useNavigate();
  const { claimId: paramId } = useParams();
  const [claimId, setClaimId] = useState(paramId || null);
  const [step, setStep] = useState(0);
  const [claim, setClaim] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    license_plate: "",
    manufacturer: "",
    model: "",
    year: "",
    vin: "",
    glass_position: "",
    damage_type: "",
    damage_notes: "",
    insurance_company: "",
    insurance_type: "",
    deductible: 0,
    claim_number: "",
    appointment_date: "",
    appointment_time: "",
  });

  const [files, setFiles] = useState({
    vehicle_front: null,
    damage_detail: null,
    damage_overview: null,
    fahrzeugschein: null,
    versicherungsschein: null,
    ausweis: null,
  });

  const [signatureData, setSignatureData] = useState(null);
  const [checks, setChecks] = useState({ datenschutz: false, agb: false, abtretung: false, widerruf_express: false });
  const [fromQuickCheck, setFromQuickCheck] = useState(false);
  const handoffFileRef = useRef(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("schnellcheck_handoff");
      if (!raw) return;
      sessionStorage.removeItem("schnellcheck_handoff");
      const h = JSON.parse(raw);
      if (h.file) {
        setFiles((p) => ({ ...p, damage_detail: h.file }));
        handoffFileRef.current = h.file;
      }
      if (h.result) {
        const typeMap = { steinschlag: "steinschlag", riss: "riss" };
        const verdictLabels = {
          reparierbar: "wahrscheinlich reparierbar",
          austausch: "vermutlich Austausch nötig",
          unklar: "nicht eindeutig erkennbar",
        };
        const aiType = typeMap[(h.result.damage_type || "").toLowerCase()] || "";
        setForm((f) => ({
          ...f,
          damage_type: f.damage_type || aiType,
          damage_notes: f.damage_notes || (verdictLabels[h.result.verdict] ? `KI-Schnell-Check Ersteinschätzung: ${verdictLabels[h.result.verdict]}` : ""),
        }));
      }
      if (h.file || h.result) setFromQuickCheck(true);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (paramId) {
      api.get(`/claims/${paramId}`).then(({ data }) => {
        setClaim(data);
        setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([k]) => k in f)) }));
      }).catch(() => toast.error("Schaden nicht gefunden"));
    }
  }, [paramId]);

  const ensureClaim = async () => {
    if (claimId) return claimId;
    const { data } = await api.post("/claims", {
      flow_type: "insurance",
      customer_name: form.customer_name,
      customer_email: normalizeEmail(form.customer_email),
      customer_phone: form.customer_phone,
    });
    setClaimId(data.id);
    setClaim(data);
    if (handoffFileRef.current) {
      const f = handoffFileRef.current;
      handoffFileRef.current = null;
      api.post(`/claims/${data.id}/files`, {
        file_id: f.file_id,
        storage_path: f.storage_path,
        original_filename: f.original_filename,
        content_type: f.content_type,
        size: f.size,
        category: "damage_detail",
      }).catch(() => {});
    }
    return data.id;
  };

  const saveStep = async (patch = {}) => {
    setSaving(true);
    try {
      const id = await ensureClaim();
      const { data } = await api.patch(`/claims/${id}`, { ...form, customer_email: normalizeEmail(form.customer_email), ...patch });
      setClaim(data);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const attachFile = async (category, fileRecord) => {
    setFiles((p) => ({ ...p, [category]: fileRecord }));
    if (!fileRecord) return;
    const id = await ensureClaim();
    await api.post(`/claims/${id}/files`, {
      file_id: fileRecord.file_id,
      storage_path: fileRecord.storage_path,
      original_filename: fileRecord.original_filename,
      content_type: fileRecord.content_type,
      size: fileRecord.size,
      category,
    });
  };

  const runOCR = async (fileRecord, docType) => {
    if (!fileRecord) return;
    setOcrLoading(true);
    try {
      // Download then re-upload via OCR endpoint (or just fetch raw from storage URL)
      const res = await fetch(`${api.defaults.baseURL}/files/${fileRecord.storage_path}`);
      const blob = await res.blob();
      const form2 = new FormData();
      form2.append("file", blob, fileRecord.original_filename || "doc.jpg");
      form2.append("doc_type", docType);
      const { data } = await api.post("/ai/ocr", form2, { headers: { "Content-Type": "multipart/form-data" } });
      const updates = {};
      if (docType === "vehicle_registration") {
        if (data.license_plate) updates.license_plate = data.license_plate;
        if (data.manufacturer) updates.manufacturer = data.manufacturer;
        if (data.model) updates.model = data.model;
        if (data.year) updates.year = String(data.year);
        if (data.vin) updates.vin = data.vin;
        if (data.hsn) updates.hsn = data.hsn;
        if (data.tsn) updates.tsn = data.tsn;
      } else {
        if (data.insurance_company) updates.insurance_company = data.insurance_company;
        if (data.policy_number) updates.claim_number = data.policy_number;
      }
      setForm((f) => ({ ...f, ...updates }));
      toast.success("Daten erkannt — bitte prüfen");
    } catch (e) {
      toast.error("OCR fehlgeschlagen");
    } finally {
      setOcrLoading(false);
    }
  };

  const next = async () => {
    if (step === 0) {
      if (!form.customer_name.trim()) return toast.error("Bitte geben Sie Ihren Namen an.");
      if (!isValidEmail(form.customer_email)) return toast.error("Bitte geben Sie eine gültige E-Mail-Adresse an (z.B. max@beispiel.de) — sonst erreichen Sie unsere Bestätigungen nicht.");
      if (!form.customer_phone.trim()) return toast.error("Bitte geben Sie Ihre Telefonnummer an.");
    }
    try {
      await saveStep();
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitSignature = async () => {
    if (!signatureData) {
      toast.error("Bitte unterschreiben Sie zuerst.");
      return;
    }
    if (!checks.datenschutz || !checks.agb || !checks.abtretung || !checks.widerruf_express) {
      toast.error("Bitte alle Bestätigungen ankreuzen.");
      return;
    }
    setSaving(true);
    try {
      const id = await ensureClaim();
      await api.post(`/claims/${id}/signature`, { image_base64: signatureData });
      toast.success("Unterschrift gespeichert");
      setStep((s) => s + 1);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Konnte Unterschrift nicht speichern";
      toast.error(`Fehler: ${msg}`);
      console.error("Signature error:", e);
    } finally {
      setSaving(false);
    }
  };

  const startPayment = async () => {
    setSaving(true);
    try {
      const id = await ensureClaim();
      await saveStep();
      if (!form.deductible || Number(form.deductible) === 0) {
        toast.success("Keine Selbstbeteiligung — Schaden vollständig erfasst!");
        navigate(`/status/${claim?.tracking_code}`);
        return;
      }
      const { data } = await api.post("/payments/checkout/session", {
        claim_id: id,
        payment_type: "deductible",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Zahlung konnte nicht gestartet werden");
    } finally {
      setSaving(false);
    }
  };

  const completeWithoutPayment = async () => {
    setSaving(true);
    try {
      await ensureClaim();
      await saveStep();
      toast.success("Schaden vollständig erfasst!");
      navigate(`/status/${claim?.tracking_code}`);
    } catch {} finally { setSaving(false); }
  };

  const copyTracking = () => {
    if (claim?.tracking_code) {
      navigator.clipboard.writeText(claim.tracking_code);
      toast.success("Code kopiert");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-40 md:pb-20">
      <SeoHead
        title="Glasschaden online melden — in 5 Minuten | Servus Autoglas"
        description="Steinschlag oder Glasbruch? Schaden online melden, Fotos hochladen, Termin sichern — Abwicklung direkt mit Ihrer Versicherung. Ludwigshafen, Mannheim & Umgebung."
        canonical={`${SITE_URL}/schaden-melden`}
      />
      <Header />
      <StepProgress steps={STEPS} current={step} />
      {fromQuickCheck && (
        <div className="max-w-2xl mx-auto px-4 mt-4">
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2"
            data-testid="quickcheck-handoff-banner"
          >
            <CheckCircle size={18} weight="fill" className="shrink-0" />
            Foto & KI-Ersteinschätzung aus dem Schnell-Check übernommen — Ihr Schadenfoto ist bereits angehängt.
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {claim?.tracking_code && (
          <div className="mb-6 p-4 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Ihr Tracking-Code</p>
              <p className="text-lg font-bold text-slate-900" data-testid="claim-tracking-code">{claim.tracking_code}</p>
            </div>
            <Button variant="outline" size="sm" onClick={copyTracking} data-testid="copy-tracking">
              <Copy size={16} className="mr-1.5" /> Kopieren
            </Button>
          </div>
        )}

        {/* STEP 1: Fahrzeugdaten */}
        {step === 0 && (
          <Section icon={CarProfile} title="Fahrzeugdaten" subtitle="Nur das Wichtigste – wir lesen den Rest später automatisch aus.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Kennzeichen" required>
                <Input
                  value={form.license_plate}
                  onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
                  placeholder="B-AB 1234"
                  className="h-14 text-base"
                  data-testid="input-license-plate"
                />
              </Field>
              <Field label="Hersteller">
                <AutocompleteInput
                  value={form.manufacturer}
                  onChange={(val) => setForm({ ...form, manufacturer: val })}
                  onSelect={(val) => setForm({ ...form, manufacturer: val, model: "" })}
                  getSuggestions={(q) => searchBrands(q, 10)}
                  placeholder="z.B. BMW, Ford, Audi …"
                  inputClassName="h-14 text-base"
                  testId="input-manufacturer"
                  emptyLabel="Keine passende Marke gefunden"
                />
              </Field>
              <Field label="Modell">
                <AutocompleteInput
                  value={form.model}
                  onChange={(val) => setForm({ ...form, model: val })}
                  onSelect={(val) => setForm({ ...form, model: val })}
                  getSuggestions={(q) => searchModels(form.manufacturer, q, 12)}
                  placeholder={
                    form.manufacturer
                      ? `z.B. ${searchModels(form.manufacturer, "", 1)[0] || "320d"}`
                      : "Erst Hersteller wählen"
                  }
                  disabled={!form.manufacturer}
                  inputClassName="h-14 text-base"
                  testId="input-model"
                  emptyLabel="Keine passenden Modelle gefunden"
                />
              </Field>
              <Field label="Baujahr">
                <Input
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  placeholder="2020"
                  className="h-14"
                  data-testid="input-year"
                />
              </Field>
              <Field label="VIN / Fahrgestellnummer (optional)">
                <Input
                  value={form.vin}
                  onChange={(e) => setForm({ ...form, vin: e.target.value })}
                  placeholder="WBA…"
                  className="h-14"
                  data-testid="input-vin"
                />
              </Field>
              <Field label="Telefonnummer">
                <Input
                  value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="+49 …"
                  className="h-14"
                  data-testid="input-phone"
                />
              </Field>
              <Field label="Name">
                <Input
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Max Mustermann"
                  className="h-14"
                  data-testid="input-name"
                />
              </Field>
              <Field label="E-Mail">
                <Input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  placeholder="max@beispiel.de"
                  className={`h-14 ${form.customer_email && !isValidEmail(form.customer_email) ? "border-red-400 focus-visible:ring-red-300" : ""}`}
                  data-testid="input-email"
                />
                {form.customer_email && !isValidEmail(form.customer_email) && (
                  <p className="mt-1.5 text-xs text-red-600" data-testid="email-format-hint">
                    Bitte gültige E-Mail-Adresse eingeben — sonst erreichen unsere Bestätigungen Sie nicht.
                  </p>
                )}
              </Field>
            </div>

            <div className="mt-6">
              <FileUpload
                label="Fahrzeug komplett von vorne (mit Kennzeichen sichtbar)"
                category="vehicle_front"
                hint="JPG / PNG / HEIC · max. 15 MB"
                useCamera
                onUploaded={(f) => attachFile("vehicle_front", f)}
                initialFile={files.vehicle_front}
                testid="upload-vehicle-front"
              />
            </div>
          </Section>
        )}

        {/* STEP 2: Schaden */}
        {step === 1 && (
          <Section icon={Warning} title="Schaden erfassen" subtitle="Welche Scheibe und welche Art von Schaden?">
            <Field label="Welche Scheibe?" required>
              <RadioGroup
                value={form.glass_position}
                onValueChange={(v) => setForm({ ...form, glass_position: v })}
                className="grid grid-cols-2 gap-3"
              >
                {GLASS_OPTIONS.map((o) => (
                  <Label
                    key={o.v}
                    htmlFor={`glass-${o.v}`}
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      form.glass_position === o.v ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    data-testid={`radio-glass-${o.v}`}
                  >
                    <RadioGroupItem value={o.v} id={`glass-${o.v}`} />
                    <span className="font-medium text-slate-900">{o.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </Field>

            <Field label="Art des Schadens" required>
              <RadioGroup
                value={form.damage_type}
                onValueChange={(v) => setForm({ ...form, damage_type: v })}
                className="grid grid-cols-2 gap-3"
              >
                {DAMAGE_OPTIONS.map((o) => (
                  <Label
                    key={o.v}
                    htmlFor={`dmg-${o.v}`}
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                      form.damage_type === o.v ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    data-testid={`radio-damage-${o.v}`}
                  >
                    <RadioGroupItem value={o.v} id={`dmg-${o.v}`} />
                    <span className="font-medium text-slate-900">{o.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <FileUpload
                label="Schaden fotografieren (Detail)"
                category="damage_detail"
                hint="Möglichst nah am Schaden"
                useCamera
                onUploaded={(f) => attachFile("damage_detail", f)}
                initialFile={files.damage_detail}
              />
              <FileUpload
                label="Gesamtbild Schaden"
                category="damage_overview"
                hint="Position auf der Scheibe"
                useCamera
                onUploaded={(f) => attachFile("damage_overview", f)}
                initialFile={files.damage_overview}
              />
            </div>

            <Field label="Weitere Informationen (optional)">
              <Textarea
                value={form.damage_notes}
                onChange={(e) => setForm({ ...form, damage_notes: e.target.value })}
                placeholder="Wann und wo ist der Schaden entstanden?"
                rows={4}
                data-testid="input-damage-notes"
              />
            </Field>
          </Section>
        )}

        {/* STEP 3: Dokumente */}
        {step === 2 && (
          <Section icon={FileText} title="Dokumente hochladen" subtitle="Die KI liest Ihren Fahrzeugschein automatisch aus.">
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-200 p-4 bg-white">
                <FileUpload
                  label="Fahrzeugschein"
                  category="fahrzeugschein"
                  hint="Vorderseite – Daten werden automatisch erkannt"
                  required
                  onUploaded={(f) => attachFile("fahrzeugschein", f)}
                  initialFile={files.fahrzeugschein}
                />
                {files.fahrzeugschein && (
                  <Button
                    variant="outline"
                    onClick={() => runOCR(files.fahrzeugschein, "vehicle_registration")}
                    disabled={ocrLoading}
                    className="mt-3 h-11"
                    data-testid="ocr-vehicle-registration"
                  >
                    {ocrLoading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <MagicWand size={18} className="mr-2" />}
                    Mit KI auslesen
                  </Button>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 p-4 bg-white">
                <FileUpload
                  label="Versicherungsschein"
                  category="versicherungsschein"
                  hint="JPG / PNG / PDF — Vorderseite oder komplette Police"
                  onUploaded={(f) => attachFile("versicherungsschein", f)}
                  initialFile={files.versicherungsschein}
                />
                {files.versicherungsschein && (
                  <Button
                    variant="outline"
                    onClick={() => runOCR(files.versicherungsschein, "insurance_card")}
                    disabled={ocrLoading}
                    className="mt-3 h-11"
                    data-testid="ocr-insurance"
                  >
                    {ocrLoading ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <MagicWand size={18} className="mr-2" />}
                    Versicherungsdaten erkennen
                  </Button>
                )}
              </div>

              <FileUpload
                label="Personalausweis (optional)"
                category="ausweis"
                hint="Beschleunigt die Abwicklung"
                onUploaded={(f) => attachFile("ausweis", f)}
                initialFile={files.ausweis}
              />
            </div>
          </Section>
        )}

        {/* STEP 4: Versicherung */}
        {step === 3 && (
          <Section icon={ShieldCheck} title="Versicherung" subtitle="Wir übernehmen die komplette Abwicklung direkt mit Ihrer Versicherung.">
            <Field label="Versicherung" required>
              <Input
                value={form.insurance_company}
                onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
                placeholder="z.B. HUK-Coburg"
                className="h-14"
                data-testid="input-insurance-company"
              />
            </Field>
            <Field label="Art der Versicherung" required>
              <RadioGroup
                value={form.insurance_type}
                onValueChange={(v) => setForm({ ...form, insurance_type: v })}
                className="grid grid-cols-3 gap-3"
              >
                {[
                  { v: "teilkasko", label: "Teilkasko" },
                  { v: "vollkasko", label: "Vollkasko" },
                  { v: "nicht_bekannt", label: "Nicht bekannt" },
                ].map((o) => (
                  <Label
                    key={o.v}
                    htmlFor={`ins-${o.v}`}
                    className={`flex items-center gap-2 p-4 rounded-xl border cursor-pointer ${
                      form.insurance_type === o.v ? "border-blue-500 bg-blue-50/50" : "border-slate-200 bg-white"
                    }`}
                    data-testid={`radio-insurance-${o.v}`}
                  >
                    <RadioGroupItem value={o.v} id={`ins-${o.v}`} />
                    <span className="font-medium text-slate-900 text-sm">{o.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </Field>

            <Field label="Selbstbeteiligung">
              <RadioGroup
                value={String(form.deductible)}
                onValueChange={(v) => setForm({ ...form, deductible: Number(v) })}
                className="grid grid-cols-2 sm:grid-cols-5 gap-3"
              >
                {[0, 150, 300, 500, 1000].map((d) => (
                  <Label
                    key={d}
                    htmlFor={`d-${d}`}
                    className={`flex items-center gap-2 p-4 rounded-xl border cursor-pointer justify-center ${
                      Number(form.deductible) === d ? "border-blue-500 bg-blue-50/50" : "border-slate-200 bg-white"
                    }`}
                    data-testid={`radio-deductible-${d}`}
                  >
                    <RadioGroupItem value={String(d)} id={`d-${d}`} className="sr-only" />
                    <span className="font-semibold text-slate-900">{d === 0 ? "0 €" : `${d} €`}</span>
                  </Label>
                ))}
              </RadioGroup>
            </Field>

            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 leading-relaxed">
              <strong>Direktabwicklung:</strong> Wir kontaktieren Ihre Versicherung, klären die Kostenübernahme und
              senden alle Unterlagen automatisch ein. Sie zahlen — wenn überhaupt — nur die Selbstbeteiligung.
            </div>
          </Section>
        )}

        {/* STEP 5: Abtretung + Signatur */}
        {step === 4 && (
          <Section icon={PencilSimpleLine} title="Abtretungserklärung" subtitle="Damit dürfen wir mit Ihrer Versicherung direkt abrechnen.">
            <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed max-h-64 overflow-y-auto">
              <p className="font-bold text-slate-900 mb-2">Abtretungserklärung</p>
              <p>
                Hiermit trete ich sämtliche Ansprüche aus dem bestehenden Kaskoversicherungsvertrag
                bezüglich des Glasschadens am Fahrzeug:
              </p>
              <ul className="my-3 space-y-1">
                <li><strong>Kennzeichen:</strong> {form.license_plate || "—"}</li>
                <li><strong>Versicherung:</strong> {form.insurance_company || "—"}</li>
                <li><strong>Schadennummer:</strong> {form.claim_number || "—"}</li>
              </ul>
              <p>
                an <strong>Autoglas-Schnellservice</strong> ab. Der Dienstleister ist berechtigt, die
                Schadensregulierung unmittelbar mit meiner Versicherung abzuwickeln.
              </p>
            </div>

            <Field label="Digitale Unterschrift" required>
              <SignaturePadField onChange={setSignatureData} />
            </Field>

            <div className="space-y-3">
              {[
                { k: "datenschutz", label: "Datenschutzerklärung gelesen und akzeptiert", link: "/datenschutz" },
                { k: "agb", label: "AGB akzeptiert", link: "/agb" },
                { k: "abtretung", label: "Abtretungserklärung akzeptiert", link: null },
              ].map((c) => (
                <label key={c.k} className="flex items-center gap-3 cursor-pointer" data-testid={`check-${c.k}`}>
                  <Checkbox
                    checked={checks[c.k]}
                    onCheckedChange={(v) => setChecks({ ...checks, [c.k]: !!v })}
                  />
                  <span className="text-sm text-slate-700">
                    {c.label}
                    {c.link && <> (<a href={c.link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">öffnen</a>)</>}
                  </span>
                </label>
              ))}

              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="check-widerruf_express">
                  <Checkbox
                    checked={checks.widerruf_express}
                    onCheckedChange={(v) => setChecks({ ...checks, widerruf_express: !!v })}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-amber-900 leading-relaxed">
                    <strong>Express-Termin innerhalb 14 Tagen — Widerrufsrecht-Hinweis:</strong> Ich verlange
                    ausdrücklich, dass mit der Reparatur bereits vor Ablauf der 14-tägigen Widerrufsfrist
                    begonnen wird. Mir ist bekannt, dass ich mein Widerrufsrecht verliere, sobald die Leistung
                    vollständig erbracht ist, bzw. bei Teilausführung anteilig Wertersatz schulde.{" "}
                    <a href="/widerruf" target="_blank" rel="noreferrer" className="underline">Vollständige Widerrufsbelehrung</a>.
                  </span>
                </label>
              </div>
            </div>
          </Section>
        )}

        {/* STEP 6: Termin */}
        {step === 5 && (
          <Section icon={CalendarBlank} title="Terminbuchung" subtitle="Wählen Sie ein Datum und eine Uhrzeit.">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Datum</p>
                <Calendar
                  mode="single"
                  selected={parseLocalDateString(form.appointment_date)}
                  onSelect={(d) => d && setForm({ ...form, appointment_date: toLocalDateString(d) })}
                  disabled={(date) => isDateDisabled(date)}
                  className="rounded-lg"
                  data-testid="appointment-calendar"
                />
                <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900" data-testid="booking-hint">
                  {getBookingHint()}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Uhrzeit</p>
                <div className="grid grid-cols-2 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm({ ...form, appointment_time: t })}
                      className={`h-14 rounded-xl border text-base font-semibold transition-all ${
                        form.appointment_time === t
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-900 border-slate-200 hover:border-blue-400"
                      }`}
                      data-testid={`time-slot-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-5 p-3 rounded-lg bg-slate-50 text-xs text-slate-600 border border-slate-200">
                  Die Montage dauert etwa 3–4 Stunden. Wir empfehlen einen Vormittagstermin.
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* STEP 7: Zahlung / Abschluss */}
        {step === 6 && (
          <Section icon={CreditCard} title="Zahlung" subtitle={form.deductible > 0 ? `Selbstbeteiligung: ${form.deductible} €` : "Keine Selbstbeteiligung — Sie sind fertig!"}>
            {form.deductible > 0 ? (
              <>
                <div className="space-y-3">
                  <div className="p-5 rounded-xl border border-slate-200 bg-white">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Zu zahlen heute</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">{form.deductible.toFixed(2)} €</p>
                    <p className="text-sm text-slate-500 mt-1">Selbstbeteiligung Ihrer Kaskoversicherung</p>
                  </div>
                  <div className="grid gap-2 text-sm text-slate-600">
                    <p>Wählen Sie eine Zahlungsart auf dem nächsten Schritt:</p>
                    <ul className="grid grid-cols-2 gap-2 mt-1 text-xs">
                      {["Kreditkarte", "Apple Pay", "Google Pay", "Sofortüberweisung", "PayPal"].map((p) => (
                        <li key={p} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">{p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="grid gap-3 mt-6">
                  <Button
                    onClick={startPayment}
                    disabled={saving}
                    className="h-14 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold"
                    data-testid="start-payment"
                  >
                    {saving ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : <CreditCard size={18} className="mr-2" />}
                    Jetzt online zahlen
                  </Button>
                  <Button
                    variant="outline"
                    onClick={completeWithoutPayment}
                    className="h-14 text-base"
                    data-testid="pay-onsite"
                  >
                    Vor Ort zahlen (Bar / EC)
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" />
                <h3 className="text-2xl font-bold text-slate-900 mt-3">Schaden vollständig erfasst!</h3>
                <p className="text-slate-600 mt-2">Wir kümmern uns ab jetzt um alles.</p>
                <Button
                  onClick={completeWithoutPayment}
                  className="mt-6 h-14 bg-blue-600 hover:bg-blue-700 px-8"
                  data-testid="finish-no-payment"
                >
                  Zur Status-Seite
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </div>
            )}
          </Section>
        )}

        {/* Nav buttons */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={back}
            disabled={step === 0}
            className="h-14 px-6"
            data-testid="wizard-back"
          >
            <ArrowLeft size={18} className="mr-2" /> Zurück
          </Button>

          {step === 4 ? (
            <Button
              onClick={submitSignature}
              disabled={saving || !signatureData || !checks.datenschutz || !checks.agb || !checks.abtretung || !checks.widerruf_express}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700"
              data-testid="wizard-sign-next"
            >
              {saving ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : null}
              Unterschreiben & weiter
              <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : step < STEPS.length - 1 ? (
            <Button
              onClick={next}
              disabled={saving}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700"
              data-testid="wizard-next"
            >
              {saving ? <SpinnerGap size={18} className="mr-2 animate-spin" /> : null}
              Weiter
              <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-6 animate-fade-up shadow-sm">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
        <Icon size={24} weight="duotone" color="white" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="space-y-5">{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);
